import { useTranslation } from 'react-i18next';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import theme from '../../../../theme/theme';
import {
  PERMISSION_ACTIONS,
  type CatalogueLine,
  type LinePermissions,
  type PermissionAction,
} from '../../types/rights-profile.types';
import { LINE_LABELS, LEVEL_LABEL_KEY } from '../../constants/catalogue-labels';
import { isLineDisabled } from '../../logic/permissionMatrix.logic';

interface PermissionMatrixRowProps {
  catalogueLine: CatalogueLine;
  perms: LinePermissions;
  readOnly?: boolean;
  onToggle: (action: PermissionAction, checked: boolean) => void;
}

// Custom checkbox box: thin 1.5px border, 4px radius (cf. maquette). Colour comes
// from the Checkbox `color` via currentColor, so the active/disabled/hover rules
// defined on the Checkbox sx keep driving it.
const CHECKBOX_PX = 20;
const UNCHECKED_ICON = (
  <Box
    sx={{
      width: CHECKBOX_PX,
      height: CHECKBOX_PX,
      borderRadius: '4px',
      border: '1.5px solid currentColor',
      boxSizing: 'border-box',
    }}
  />
);
const CHECKED_ICON = (
  <Box
    sx={{
      width: CHECKBOX_PX,
      height: CHECKBOX_PX,
      borderRadius: '4px',
      backgroundColor: 'currentColor',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Box
      component="svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth={3.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      sx={{ width: 13, height: 13 }}
    >
      <path d="M5 12.5l4.5 4.5L19 7" />
    </Box>
  </Box>
);

/** A sub-row (onglet / entrée) is a child line nested under a parent. */
function isSubRow(level: CatalogueLine['level']): boolean {
  return level === 'onglet' || level === 'entree';
}

/** A child line (non-empty parents) is indented under its parent. */
function indentOf(level: CatalogueLine['level']): number {
  return isSubRow(level) ? 3 : 0;
}

/**
 * One matrix row = one catalogue line: level prefix + label/icon, then one cell per
 * action. An action not applicable to the line (absent from `catalogueLine.actions`)
 * renders a non-checkable « — » (refused in 400 by the API). Non-Display checkboxes
 * are disabled while Display is off (the « no phantom right » rule is enforced in
 * `toggleAction`; this mirrors it visually); every checkbox is disabled in `readOnly`.
 */
export function PermissionMatrixRow({
  catalogueLine,
  perms,
  readOnly,
  onToggle,
}: PermissionMatrixRowProps) {
  const { t } = useTranslation('rightsProfiles');
  const { line, level, actions } = catalogueLine;
  const { i18nKey } = LINE_LABELS[line];
  const lineLocked = isLineDisabled(perms);

  return (
    <TableRow>
      <TableCell sx={{ pl: indentOf(level) + 2 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.subtle,
              textTransform: 'uppercase',
              fontSize: '0.65rem',
            }}
          >
            {t(LEVEL_LABEL_KEY(level))}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.body }}>
            {t(i18nKey)}
          </Typography>
        </Stack>
      </TableCell>
      {PERMISSION_ACTIONS.map(action => {
        const applicable = actions.includes(action);
        return (
          <TableCell key={action} align="center" padding="checkbox">
            {applicable ? (
              <Checkbox
                size="medium"
                icon={UNCHECKED_ICON}
                checkedIcon={CHECKED_ICON}
                checked={Boolean(perms[action])}
                disabled={readOnly || (action !== 'display' && lineLocked)}
                onChange={event => onToggle(action, event.target.checked)}
                sx={{
                  // clickable: light BLUE border by default, full blue on hover/checked
                  color: alpha(theme.palette.primary.main, 0.35),
                  '&.Mui-checked': { color: theme.palette.primary.main },
                  '&:hover:not(.Mui-disabled)': { color: theme.palette.primary.main },
                  // non-clickable (disabled): GREY + faded + not-allowed cursor
                  '&.Mui-disabled': {
                    color: theme.palette.divider,
                    opacity: 0.45,
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                  },
                }}
                inputProps={{
                  'aria-label': `${t(i18nKey)} — ${t(`actions.${action}`)}`,
                }}
              />
            ) : (
              <Typography component="span" sx={{ color: theme.palette.text.subtle }} aria-hidden>
                —
              </Typography>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}
